import {FC, useMemo} from 'react'
import {ColumnDef} from '@tanstack/react-table'
import {Badge, Card} from 'react-bootstrap'
import DataTable from '../../../../../ui-kit/DataTable'
import {getISO2ByISO3, getNameByISO3, shortenLargeNumber} from '../../../../libs/misc'
import {PARTNERS_TABLE_IDX, SimulationResponse} from '../core/_models'
import {calcEconPct} from '../core/_transforms'

type Row = {econ: string; total: number; pct: string; commodities: number}

const ReducedImportsTable: FC<{
  result: SimulationResponse
  onBadgeClick: (econ: string) => void
}> = ({result, onBadgeClick}) => {
  const rows: Row[] = useMemo(
    () =>
      (result.partners_affected.partners_table_data || []).map((r) => {
        const econ = r[PARTNERS_TABLE_IDX.econ]
        const total = r[PARTNERS_TABLE_IDX.totalEffect]
        return {
          econ,
          total,
          pct: calcEconPct(total, result.current_trade[econ]),
          commodities: r[PARTNERS_TABLE_IDX.commoditiesAffectedCount],
        }
      }),
    [result]
  )

  const columns: ColumnDef<Row, any>[] = [
    {
      accessorKey: 'econ',
      header: 'Economy',
      cell: (c) => (
        <>
          <span
            className={`fi fi-${(getISO2ByISO3(c.getValue()) || '').toLowerCase()} me-2`}
          ></span>
          {getNameByISO3(c.getValue())}
        </>
      ),
    },
    {
      accessorKey: 'total',
      header: 'Total loss/gain',
      cell: (c) => (
        <span className={c.getValue() < 0 ? 'kt-font-danger' : 'kt-font-success'}>
          {shortenLargeNumber(c.getValue())}
        </span>
      ),
    },
    {accessorKey: 'pct', header: '% of current imports'},
    {
      accessorKey: 'commodities',
      header: 'Commodities',
      enableSorting: false,
      cell: (c) => (
        <Badge
          bg='primary'
          className='cursor-pointer text-white'
          onClick={() => onBadgeClick(c.row.original.econ)}
        >
          {c.getValue()} commodities
        </Badge>
      ),
    },
  ]

  return (
    <Card className='mb-5' id='pref-loss-economies-table'>
      <Card.Header className='card-header-stretch'>
        <Card.Title>
          <h3>Reduced imports from reporter by trade partners</h3>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <DataTable
          data={rows}
          columns={columns}
          enablePagination={rows.length > 25}
          initialSortBy={{id: 'total', desc: false}}
        />
      </Card.Body>
    </Card>
  )
}

export default ReducedImportsTable
